import { Euro, WidthFull } from "@mui/icons-material";
import { TabContext, TabPanel } from "@mui/lab";
import { Skeleton } from "@mui/material";
import classNames from "classnames";
import { handleScrollToErrors } from "components/NewForm/utils";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import SelectFormComponent from "components/shared/FormComponent/SelectFormComponent";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import StyledButton from "components/shared/StyledButton";
import TitleSeparation from "components/shared/TitleSeparation";
import DropZoneCSV from "components/shared/UploadCsv";
import { transferWorkLabel } from "containers/TransferTermFormContainer/utils";
import { getCountries } from "countries";
import { formatLocaleDateString, isObjectEmpty, replaceComma } from "helpers/utils";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { uploadFile } from "store/documentStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchTransferees, fetchTransfereeTerms } from "store/transfereesStore";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import { addTransfereeRights, ETransferTermSalesStatus } from "store/transferTermSalesStore";
import type { ITransferTerm } from "store/transferTermsStore";
import { ETransferTermStatus } from "store/transferTermsStore";

import variables from "../../../assets/styles/_variables.module.scss";
import { getListOfCountries } from "../utils";
import styles from "./style.module.scss";

interface IAddTransfereeRightsModal {
    /**
     * All existing payments.
     */
    transferTermSales: ITransferTermSale[];
    handleClose: () => void;
}

interface IFormData {
    transfereeId: number | null;
    sales: { salesId: number; grossAmount: number | string; netAmount: number | string }[];
    statementUrl: string;
}

const salesStatusLabels = {
    [ETransferTermSalesStatus.DUE_LATER]: t("TransferTerms.SalesStatus.Soon"),
    [ETransferTermSalesStatus.DUE_NEXT]: t("TransferTerms.SalesStatus.Soon"),
    [ETransferTermSalesStatus.WAITING_STATEMENT]: t("TransferTerms.SalesStatus.WaitingStatement"),
    [ETransferTermSalesStatus.LATE_STATEMENT]: t("TransferTerms.SalesStatus.WaitingStatement"),
};

/**
 * Within TransferTerms, modal to enter rights per payment connected to one transferee.
 */
const AddTransfereeRightsModal = ({ transferTermSales, handleClose }: IAddTransfereeRightsModal) => {
    const dispatch = useAppDispatch();
    const [t] = useTranslation();
    const { transferees, transfereesStatus } = useAppSelector((state) => state.transferees);

    const [tabValue, setTabValue] = useState("0");
    const [transfereeSales, setTransfereeSales] = useState<ITransferTermSale[]>([]);
    const [transfereeTerms, setTransfereeTerms] = useState<ITransferTerm[]>([]);
    const [loadingTerms, setLoadingTerms] = useState(false);
    const [files, setFiles] = useState<File[]>();
    const [isFileLoading, setIsFileLoading] = useState(false);
    const [submitPending, setSubmitPending] = useState(false);

    const defaultValues = {
        transfereeId: null,
        sales: [] as { salesId: number; grossAmount: number | string; netAmount: number | string }[],
        statementUrl: "",
    };

    const {
        control,
        watch,
        setValue,
        formState: { errors },
        handleSubmit,
        trigger,
    } = useForm({ defaultValues, mode: "all" });
    const chosenTransfereeId = watch("transfereeId");
    const chosenSales = watch("sales");

    const { fields } = useFieldArray({ control, name: "sales" });

    /**
     * Format transferees array to use in Select component.
     */
    const transfereeOptions = transferees
        .filter((item) => !!item.transferTermCount && item.transferTermCount > 0)
        .map((item) => ({ value: item.id, label: item.name }));

    const formatSalesOptions = (termSales: ITransferTermSale[]) => {
        return termSales.map((sale) => ({
            value: sale.id,
            label: `${formatLocaleDateString({ date: sale.dueDate })} - ${salesStatusLabels[sale.status]}`,
        }));
    };

    const transfereeHasPaymentsPending = useMemo(() => {
        return !!transfereeTerms.length && !loadingTerms && transfereeSales.length > 0;
    }, [transfereeTerms, transfereeSales, loadingTerms]);

    const onSubmit = async (data: IFormData) => {
        setSubmitPending(true);
        // Remove all sales where no info was set or one info missing + format amounts correctly.
        const salesToSave = data.sales
            .filter((sale) => sale.salesId && sale.grossAmount && sale.netAmount)
            .map((sale) => {
                return {
                    ...sale,
                    grossAmount: +replaceComma(sale.grossAmount) * 100,
                    netAmount: +replaceComma(sale.netAmount) * 100,
                };
            });

        const req = {
            ...data,
            sales: salesToSave,
        };

        if (!chosenTransfereeId) return;
        try {
            const response = await dispatch(addTransfereeRights({ transfereeId: chosenTransfereeId, data: req }));
            if (response.meta.requestStatus === "fulfilled") {
                handleClose();
            }
        } finally {
            setSubmitPending(false);
        }
    };

    useEffect(() => {
        if (transfereesStatus === "idle") {
            dispatch(fetchTransferees({ options: { limit: 100 } }));
        }
    }, [transferees, transfereesStatus]);

    useEffect(() => {
        if (chosenTransfereeId) {
            // Reset values whenever they switch transferees:
            setValue("sales", []);
            setValue("statementUrl", "");

            setLoadingTerms(true); // Used to display skeleton

            // Select sales payments which are still waiting for statement and need to enter rights.
            const sales = transferTermSales.filter(
                (item) =>
                    item.transfereeId === chosenTransfereeId &&
                    item.type === "sales" &&
                    [1, 2, 3].includes(item.status),
            );
            if (sales) {
                setTransfereeSales(sales);
            }

            // Fetches terms attached to transferee chosen.
            dispatch(fetchTransfereeTerms({ transfereeId: chosenTransfereeId, options: {} })).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setTransfereeTerms(
                        resp.payload.entities.filter(
                            (item) =>
                                [ETransferTermStatus.UP_TO_DATE, ETransferTermStatus.EXPIRED_SOON].includes(
                                    item.status,
                                ) && sales.some((sale) => sale.transferTermId === item.id),
                        ),
                    );
                    setLoadingTerms(false);
                }
            });
        }
    }, [chosenTransfereeId]);

    // Save url into statementUrl property once file chosen.
    useEffect(() => {
        if (files && files.length > 0) {
            setIsFileLoading(true);
            const file: File = files[0];
            const fileName = file.name ? file.name : "";
            const formData = new FormData();
            formData.append("file", file, fileName);

            dispatch(uploadFile({ formData })).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setValue(`statementUrl`, resp.payload.imagePath, { shouldDirty: true });
                    setIsFileLoading(false);
                }
            });
        }
    }, [files]);

    const handleNextStep = async () => {
        const isValid = await trigger();
        if (!isValid || !isObjectEmpty(errors)) {
            handleScrollToErrors(errors);
            return;
        }
        setTabValue("1");
    };

    return (
        <form className={styles["transferee-rights-modal"]} onSubmit={handleSubmit(onSubmit)}>
            <TabContext value={tabValue}>
                <TabPanel value="0" className={styles["first-tab"]} style={{ display: tabValue !== "0" ? "none" : "" }}>
                    <div style={{ flex: 1 }}>
                        <SelectFormComponent
                            control={control}
                            controlName="transfereeId"
                            label={t("TransferTerms.AddRightsModal.ChooseTransferee")}
                            items={transfereeOptions}
                            sx={{ height: "30px" }}
                            labelStyle={{ fontSize: "13px" }}
                        />

                        {chosenTransfereeId ? (
                            <div className={styles["terms-list"]}>
                                <TitleSeparation
                                    title={t("TransferTerms.Contracts")}
                                    titleStyle={{ fontSize: "14px", color: variables["foreground"] }}
                                />
                                <div className={styles["terms-info"]}>
                                    <span>{t("TransferTerms.AddRightsModal.PaymentInfo1")}</span>
                                    <span>{t("TransferTerms.AddRightsModal.PaymentInfo2")}</span>
                                </div>
                                {loadingTerms && (
                                    <div className={styles["terms-skeleton"]}>
                                        <Skeleton width={50} />
                                        <Skeleton width={90} />
                                        <Skeleton width={110} />
                                        <div style={{ display: "flex", gap: "1rem" }}>
                                            <Skeleton width={300} />
                                            <Skeleton width={150} />
                                            <Skeleton width={150} />
                                        </div>
                                    </div>
                                )}

                                {!transfereeTerms.length && !loadingTerms && (
                                    <span className={styles["sale-info"]}>
                                        {t("TransferTerms.AddRightsModal.NoActiveContract")}
                                    </span>
                                )}
                                {!loadingTerms && transfereeHasPaymentsPending ? (
                                    <ScrollShadowWrapper>
                                        {transfereeTerms.length &&
                                            transfereeTerms.map((term, i) => {
                                                const termSales = transfereeSales.filter(
                                                    (item) => item.transferTermId === term.id,
                                                );
                                                const isRowLast = transfereeTerms.length - 1 === i;
                                                return (
                                                    <div
                                                        className={classNames({
                                                            [styles["term-row"]]: true,
                                                            [styles["last-row"]]: isRowLast,
                                                        })}
                                                        key={`term-${term.id}`}
                                                    >
                                                        <>
                                                            <div className={styles["term-info"]}>
                                                                <span className={styles["title-name"]}>
                                                                    {transferWorkLabel(term)}
                                                                </span>
                                                                <span className={styles["details"]}>
                                                                    {t("TransferTerms.AddRightsModal.Country", {
                                                                        countries: getListOfCountries(
                                                                            term.countryCodes,
                                                                            getCountries(),
                                                                        ),
                                                                    })}
                                                                </span>
                                                                <span className={styles["details"]}>
                                                                    {t("TransferTerms.AddRightsModal.Dates")}
                                                                    {formatLocaleDateString({
                                                                        date: term.startDate as Date,
                                                                    })}{" "}
                                                                    {" ↔︎ "}{" "}
                                                                    {formatLocaleDateString({
                                                                        date: term.expirationDate as Date,
                                                                    })}
                                                                </span>
                                                            </div>
                                                            <div className={styles["term-data"]}>
                                                                <SelectFormComponent
                                                                    control={control}
                                                                    controlName={`sales.${i}.salesId`}
                                                                    label={t(
                                                                        "TransferTerms.AddRightsModal.ChoosePayment",
                                                                    )}
                                                                    items={formatSalesOptions(termSales)}
                                                                    sx={{ height: "30px", minWidth: "300px" }}
                                                                    labelStyle={{ fontSize: "13px" }}
                                                                />
                                                                <NumberFormComponent
                                                                    control={control}
                                                                    sx={{ maxWidth: "190px" }}
                                                                    controlName={`sales.${i}.grossAmount`}
                                                                    label={t(
                                                                        "TransferTerms.TermTable.RoyaltiesCreated",
                                                                    )}
                                                                    labelStyle={{ fontSize: "13px" }}
                                                                    icon={<Euro sx={{ fontSize: 13 }} />}
                                                                    float
                                                                    error={
                                                                        errors.sales && errors.sales[i]
                                                                            ? errors.sales[i]?.grossAmount
                                                                            : null
                                                                    }
                                                                />
                                                                <NumberFormComponent
                                                                    control={control}
                                                                    controlName={`sales.${i}.netAmount`}
                                                                    label={t("TransferTerms.TermTable.RoyaltiesPaid")}
                                                                    labelStyle={{ maxWidth: "190px" }}
                                                                    icon={<Euro sx={{ fontSize: 13 }} />}
                                                                    float
                                                                    sx={{ width: "100%" }}
                                                                    error={
                                                                        errors.sales && errors.sales[i]
                                                                            ? errors.sales[i]?.netAmount
                                                                            : null
                                                                    }
                                                                />
                                                            </div>
                                                        </>
                                                    </div>
                                                );
                                            })}
                                    </ScrollShadowWrapper>
                                ) : (
                                    !loadingTerms &&
                                    !transfereeHasPaymentsPending && (
                                        <span className={styles["sale-info"]}>
                                            {t("TransferTerms.AddRightsModal.AlreadySet")}
                                        </span>
                                    )
                                )}
                            </div>
                        ) : (
                            <div className={styles["terms-info"]}>
                                <span>{t("TransferTerms.AddRightsModal.TransfereeInfo")}</span>
                            </div>
                        )}
                    </div>

                    <div className={styles["modal-buttons"]}>
                        <StyledButton
                            buttonType="primary"
                            type="button"
                            onClick={handleNextStep}
                            disabled={!chosenTransfereeId || !transfereeTerms.length}
                        >
                            {t("TransferTerms.AddRightsModal.Continue")}
                        </StyledButton>
                    </div>
                </TabPanel>
                <TabPanel
                    value="1"
                    className={styles["second-tab"]}
                    style={{ display: tabValue !== "1" ? "none" : "" }}
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: "5px", flex: 1 }}>
                        <span className={styles["dropzone-info"]}>
                            {t("TransferTerms.AddRightsModal.AttachStatement")}
                        </span>
                        <DropZoneCSV
                            setFile={setFiles}
                            file={files}
                            acceptedFilesTypeList={{ "application/pdf": [".pdf"] }}
                        />
                    </div>
                    <div className={styles["modal-buttons"]}>
                        <StyledButton buttonType="secondary" type="button" onClick={() => setTabValue("0")}>
                            {t("TransferTerms.AddRightsModal.Return")}
                        </StyledButton>
                        <StyledButton buttonType="primary" type="submit" loading={isFileLoading || submitPending}>
                            {t("TransferTerms.AddRightsModal.Validate")}
                        </StyledButton>
                    </div>
                </TabPanel>
            </TabContext>
        </form>
    );
};

export default AddTransfereeRightsModal;
